import { ChangeDetectorRef, Component } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { I18nService } from '@recruitee/i18n';
import { IntercomService } from '@recruitee/intercom-integration';
import { CompanyVerificationRequestStatus } from '@recruitee/spam-verification-data';
import { SpamVerificationService } from '@recruitee/spam-verification-logic';
import type { IconName } from '@recruitee/user-interface';
import { ButtonComponent, EmptyStateComponent } from '@recruitee/user-interface';

import { SpamVerificationDialogService } from '../../services/spam-verification-dialog.service';

export type SpamVerificationEmptyStateConfig = {
  title: string;
  desc: string;
  cta?: {
    label: string;
    callback: () => void;
  };
  iconName: IconName;
};

@Component({
  selector: 'rt-spam-verification-empty-state',
  templateUrl: './spam-verification-empty-state.component.html',
  styleUrls: ['./spam-verification-empty-state.component.less'],
  imports: [ButtonComponent, EmptyStateComponent],
})
export class SpamVerificationEmptyStateComponent {
  public emptyStateConfig: SpamVerificationEmptyStateConfig;

  private emptyStateConfigOptions: Record<
    CompanyVerificationRequestStatus,
    SpamVerificationEmptyStateConfig
  > = {
    [CompanyVerificationRequestStatus.Approved]: {
      title: this.i18n.translate('app.spam_verification.status.approved.title'),
      desc: '',
      iconName: 'check',
    },
    [CompanyVerificationRequestStatus.FormSubmitted]: {
      title: this.i18n.translate('app.spam_verification.status.submitted.title'),
      desc: this.i18n.translate('app.spam_verification.status.submitted.desc'),
      iconName: 'upgrade',
    },
    [CompanyVerificationRequestStatus.Pending]: {
      cta: {
        label: this.i18n.translate('app.spam_verification.status.pending.cta_label'),
        callback: () => this.spamVerificationDialogService.open(),
      },
      title: this.i18n.translate('app.spam_verification.status.pending.title'),
      desc: this.i18n.translate('app.spam_verification.status.pending.desc'),
      iconName: 'verified-user',
    },
    [CompanyVerificationRequestStatus.Rejected]: {
      cta: {
        label: this.i18n.translate('app.spam_verification.status.rejected.cta_label'),
        callback: () => this.intercomService.startNewMessage(),
      },
      title: this.i18n.translate('app.spam_verification.status.rejected.title'),
      desc: this.i18n.translate('app.spam_verification.status.rejected.desc'),
      iconName: 'error',
    },
  };

  private ngOnDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private spamVerificationService: SpamVerificationService,
    private spamVerificationDialogService: SpamVerificationDialogService,
    private intercomService: IntercomService,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  public ngOnInit(): void {
    this.spamVerificationService.selectSpamVerificationStatus$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(status => {
        this.emptyStateConfig = this.emptyStateConfigOptions[status];
        this.cdRef.detectChanges();
      });
  }
  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
